import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { X, Cookie } from 'lucide-react';
import { setCookie, getCookie } from '@/lib/cookies';
import { cookie } from '@/lib/config';
import { useTranslation } from 'react-i18next';

export const CookieConsent = () => {
  const { t } = useTranslation('common');
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const consent = getCookie('cookie_consent');
    if (!consent) {
      // Show banner after a delay to not block initial render
      const timer = setTimeout(() => setShowBanner(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    setCookie('cookie_consent', 'accepted', {
      expires: 365,
      path: '/',
      domain: cookie.domain,
      secure: cookie.secure,
      sameSite: cookie.sameSite,
    });
    setShowBanner(false);
  };

  const handleDecline = () => {
    setCookie('cookie_consent', 'declined', {
      expires: 365,
      path: '/',
      domain: cookie.domain,
      secure: cookie.secure,
      sameSite: cookie.sameSite,
    });
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[200] p-4 animate-in slide-in-from-bottom-5">
      <Card className="max-w-4xl mx-auto p-6 shadow-2xl border-2">
        <div className="flex items-start gap-4">
          <div className="p-2 rounded-full bg-primary/10 shrink-0">
            <Cookie className="h-5 w-5 text-primary" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg mb-2">
              {t('cookieConsent.title', 'We use cookies')}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('cookieConsent.description', 
                'We use cookies to enhance your experience, analyze site traffic, and personalize content. By clicking "Accept", you consent to our use of cookies.'
              )}
            </p>
            
            <div className="flex flex-wrap gap-3">
              <Button onClick={handleAccept} size="sm">
                {t('cookieConsent.accept', 'Accept All')}
              </Button>
              <Button onClick={handleDecline} variant="outline" size="sm">
                {t('cookieConsent.decline', 'Decline')}
              </Button>
              <Button variant="ghost" size="sm" asChild>
                <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                  {t('cookieConsent.learnMore', 'Learn More')}
                </a>
              </Button>
            </div>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={() => setShowBanner(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    </div>
  );
};
